import React, { useMemo } from 'react'
import { Heading } from './Heading'
import { Flex, Box, Grid, Text } from './Primitives'
import { Logo } from './Logo'
import { Divider } from './Divider'
import styled from 'styled-components'
import { layout, typography, color, space } from 'styled-system'
import shouldForwardProp from '@styled-system/should-forward-prop'
import { FATHOM_EVENTS, fathomEvent } from './fathomEvents'

const Section = ({ heading, children }) => (
	<Flex flexDirection="column" gap={2}>
		<Heading as="h4" fontSize={1}>{heading}</Heading>
		<Flex flexDirection="column" >{children}</Flex>
	</Flex>
)

const FooterLinks = ({
	currentSite,
	accountsExternal,
	accountsURL,
	homeExternal,
	homeURL,
	LinkComponent,
}) => {
	const APP_LINKS = useMemo(() => {
		return {
			Bundle: [
				{ to: `${homeURL}/bundle`, label: 'Bundle Home' },
				{
					to: `https://itunes.apple.com/us/app-bundle/id1797219065?mt=8`,
					external: true,
					label: 'Get the Bundle',
					event: FATHOM_EVENTS.bundle,
				},
				{
					to: `${homeURL}/support/account/bonobo-bundle-membership/`,
					label: 'Bundle FAQ',
				},
			],
			Timepage: [
				{ to: `${homeURL}/timepage`, label: 'Timepage Home' },
				{
					to: `https://apps.apple.com/au/app/timepage-by-moleskine-studio/id989178902`,
					external: true,
					label: 'Download Timepage',
					event: FATHOM_EVENTS.timepageIos,
				},
				{ to: `${homeURL}/support/timepage/`, label: 'Timepage User Guide' },
			],
			Actions: [
				{ to: `${homeURL}/actions`, label: 'Actions Home' },
				{
					to: `https://apps.apple.com/app/apple-store/id1227402276?pt=542023&ct=mdshome&mt=8`,
					external: true,
					label: 'Download Actions',
					event: FATHOM_EVENTS.actionsIos,
				},
				{
					to: `https://actions.bonobolabs.com/`,
					external: true,
					label: 'Use Actions Web',
					event: FATHOM_EVENTS.actionsWeb,
				},
				{ to: `${homeURL}/support/actions/`, label: 'Actions User Guide' },
			],
			Flow: [
				{ to: `${homeURL}/flow`, label: 'Flow Home' },
				{
					to: `https://apps.apple.com/app/apple-store/id1271361459?pt=542023&ct=mdshome&mt=8`,
					external: true,
					label: 'Download Flow',
					event: FATHOM_EVENTS.flowIos,
				},
				{
					to: `https://flowapp.bonobolabs.com/`,
					external: true,
					label: 'Use Flow Web',
					event: FATHOM_EVENTS.flowWeb,
				},
				{ to: `${homeURL}/support/flow/`, label: 'Flow User Guide' },
			],
			Overlap: [
				{ to: `${homeURL}/overlap`, label: 'Overlap Home' },
				{
					to: `https://apps.apple.com/au/app/overlap-by-moleskine-studio/id1516950324`,
					external: true,
					label: 'Download Overlap',
					event: FATHOM_EVENTS.overlapIos,
				},
				{ to: `${homeURL}/support/overlap/`, label: 'Overlap User Guide' },
			],
		}
	}, [homeURL])

	return (
		<Grid
			gridRowGap={4}
			gridColumnGap={3}
			gridTemplateColumns={['repeat(2, 1fr)', 'repeat(auto-fit, minmax(150px, 1fr))']}
			gridAutoRows="auto"
		>
			{Object.keys(APP_LINKS).map((appLinkKey) => {
				const key = `footer--appsection--${appLinkKey.toLowerCase()}`
				const appLinks = APP_LINKS[appLinkKey]
				return (
					<Section key={key} heading={appLinkKey}>
						{appLinks.map(({ to, label, external = homeExternal, event, ...rest }) => {
							const linkKey = `footer--appsection--${appLinkKey}--${label}`
							return (
								<LinkComponent
									key={linkKey}
									to={to}
									external={external}
									fontSize={0}
									color="bluegray"
									onClick={() => {
										if (event) {
											fathomEvent(currentSite, event, 'footer')
										}
									}}
									{...rest}
								>
									{label}
								</LinkComponent>
							)
						})}
					</Section>
				)
			})}

			<Section heading="Account">
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external={accountsExternal}
					to={`${accountsURL}/account`}
				>
					Manage Account
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external={accountsExternal}
					to={`${accountsURL}/login`}
				>
					Create Account
				</LinkComponent>
			</Section>
			<Section heading="Community">
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external={accountsExternal}
					to={`${accountsURL}/feedback`}
				>
					Vote for Features
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external
					to="https://www.instagram.com/bonobo"
					newTab
				>
					Instagram
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external
					to="https://beta-signup.bonobolabs.com/"
				>
					Bonobo Beta Signup
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external
					to="https://beta-slack-invite.bonobolabs.com/"
				>
					Bonobo Slack Community
				</LinkComponent>
			</Section>

			<Section heading="Bonobo">
				<LinkComponent fontSize={0} color="bluegray" external to={homeURL} newTab>
					Bonobo Home
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external={homeExternal}
					to={`${homeURL}/support`}
					exact
				>
					Support
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external={homeExternal}
					to={`${homeURL}/articles`}
				>
					Articles
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external={homeExternal}
					to={`${homeURL}/press`}
				>
					Press
				</LinkComponent>
				<LinkComponent
					fontSize={0}
					color="bluegray"
					external={homeExternal}
					to={`${homeURL}/contact`}
				>
					Contact Us
				</LinkComponent>
			</Section>
		</Grid>
	)
}

const Bullet = () => <Text color="bluegray">&nbsp;&bull;&nbsp;</Text>

const DisplayFlex = styled(Flex)
	.withConfig({
		shouldForwardProp,
	})
	.attrs({
		display: ['block', 'flex'],
	})(layout)

const FooterBottom = ({ homeExternal, homeURL, children, LinkComponent }) => (
	<DisplayFlex alignItems="center" mt={[4, 3]}>
		{children}
		<Box mx="auto" />
		<Flex alignItems="center" mt={[4, 0]}>
			<LinkComponent
				fontSize={0}
				external={homeExternal}
				to={`${homeURL}/privacy`}
				mr={2}
				lineHeight={1}
				color="bluegray"
			>
				Privacy & Terms
			</LinkComponent>
		</Flex>
	</DisplayFlex>
)

const CopyrightText = styled('div').withConfig({ shouldForwardProp })(color, typography, space)

const Copyright = ({ copyright = 'Bonobo Pty Ltd', LinkComponent }) => (
	<CopyrightText color="bluegray" fontSize={10} mt={[1, 3]}>
		Copyright © {new Date().getFullYear()} {copyright}
		<Bullet />
		<Box style={{ display: 'inline-block' }}>
			Made with ♥ by&nbsp;
			<LinkComponent
				color="bluegray"
				external
				to="https://bonobolabs.com"
				fontSize={10}
				inline
				newTab
			>
				Bonobo 🦍
			</LinkComponent>
		</Box>
	</CopyrightText>
)

/**
 * Footer component.
 *
 * This component renders the footer section of the website. It includes links to the home and accounts pages,
 * a logo, and copyright information. The links can be customized to be internal or external based on the `currentSite` prop.
 *
 * @component
 * @example
 * // Usage example:
 * <Footer currentSite="home" LinkComponent={YourLinkComponent} />
 *
 * @param {Object} props - The props that are passed to the component.
 * @param {string} [props.bg='sidebarblue'] - The background color of the footer.
 * @param {number} [props.maxWidth=900] - The maximum width of the footer container.
 * @param {string} [props.currentSite] - The current site context, which determines the behavior of the links.
 * @param {string} [props.accountsURL='https://accounts.bonobolabs.com'] - The URL for the accounts page.
 * @param {string} [props.homeURL='https://bonobolabs.com'] - The URL for the home page.
 * @param {React.ReactNode} [props.children] - The children to render inside the footer.
 * @param {Array} [props.py=[4, 5]] - The vertical padding of the footer container.
 * @param {Array} [props.px=[4, 5]] - The horizontal padding of the footer container.
 * @param {React.ComponentType} props.LinkComponent - The custom link component to wrap the links.
 * @returns {JSX.Element} The Footer component.
 */
export const Footer = ({
	bg = 'sidebarblue',
	maxWidth = 900,
	currentSite,
	accountsURL = 'https://accounts.bonobolabs.com',
	homeURL = 'https://bonobolabs.com',
	children,
	py = [4, 5],
	px = [4, 5],
	LinkComponent,
	...props
}) => {
	// TODO find a nicer way than this
	let accountsExternal = true
	let homeExternal = true

	if (currentSite === 'home') {
		homeExternal = false
		homeURL = ''
	}
	if (currentSite === 'accounts') {
		accountsExternal = false
		accountsURL = ''
	}

	const linksData = {
		accountsExternal,
		accountsURL,
		homeExternal,
		homeURL,
	}

	return (
		<Box as="footer" bg={bg}>
			<Box {...props} maxWidth={maxWidth} px={px} py={py}>
				<FooterLinks currentSite={currentSite} {...linksData} LinkComponent={LinkComponent} />
				<Divider borderBottom="2px solid" borderColor="lightgreyborder" />
				<FooterBottom {...linksData} LinkComponent={LinkComponent}>
					{children || <Logo LinkComponent={LinkComponent} />}
				</FooterBottom>
				<Copyright copyright="Bonobo Pty Ltd" LinkComponent={LinkComponent} />
			</Box>
		</Box>
	)
}
